/**
 * Urban Studio Life — Daymark Uptown
 *
 * Mirrors the live Daymark Urban Studio Life page
 * (https://www.daymarkuptown.com/floor-plans/urban-studio-life) using the
 * Weidner Classic two-column specialty-plan layout:
 *   LEFT: EXPERIENCE / URBAN STUDIO LIVING + 4-up gallery + features
 *   RIGHT: studio plan card (name, pricing, floor-plan image, CTAs)
 */

import { useMemo, useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useCollectionBinding,
  useContentToken,
  useEditMode,
  useSlotGroup,
  EditableCollectionBinding,
  EditableToken,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal, useStaggerReveal } from '../hooks/useScrollReveal';

const iconKitchen = "data:image/png;base64,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";
const iconSteel = "data:image/png;base64,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";
const iconClosets = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAI0AAABRCAYAAADvoiXxAAAACXBIWXMAAAsTAAALEwEAmpwYAAAF8klEQVR4nO2da4hVVRTHf77yWaSlJUpFD4r8EEiYSVONPbQYsiyD6ENQEB2oaJMRoRURQkFmH4KKihwzx75UvpoP0YMUo5c9BqkoejM1WfaSano4sW3d2ByO4zzOufuce/8fFtzL2nf2vWf95r/W3vucvWlLHDJdg7ZBXINSAgOcDXwB/Ar0yPgNeCB2XEoNjYFzGfAd0CdjPXBY7JiUHhoD5wJgG7ATeL/JrMt+9yPAIbFjURloDJzDgZnAjCa0mcBBsWNQRWjuArYAzzWZbQLWACfHjkEVodnaxDXNT8A5sWNQRWheKUHwYtku4KzYMagiNE8DvSUIoKBJqgPNoVYUzgYuB5YDj/HfMLSjAHvKaordKVBeAFYBK3O0+4D7gQeBd6Q0xQB0LHBUHUA9AngrFcQlwBjgOOD4nOwE+02jrOBXeiogmKcDVxU9DLWhbvo/fzEwB/gc+AbozsG+BT4zeO4QNMUFdFokaC6x5Y29Odc3f/thtaVdKU3RaaRAaI4EdmRA0wL8kTM0e4CTgNsFTXEBPQaYChxckE2yekPQJPuJQcWAGQE8YUWqX5PabvM4fsb4JZsIfNXMv34Z2NyPz3/uxZTP/73XgF+kNK760Bg4K23uxk+xPx5M/u0wCDrNNgJvmu9dYEPg8+3eMF+Xta35Ntjn/hE0rmGgmeXTSPD+POAHYAEwHphgNg5otcBfDIwNfL7dPOB74AprW/ONtb+VnqdRTZNUF5oTfZBT0PgAz9/PEN0HfGGG71SDZnGG70zgR0HjGkZpfJE6IXi/0KA5P6Nti0HTluGbawq1JMM33xYLpTRJY0BzdEppatAsGCQ0XoUETTKEGFR0DuX/yT2rW3yaacloO8egyVKhU2wVeVGGT0qTNBY0/k6+MfZ6or/h2kY6dwOjg3Z+PedOg+be2mcC3zKbjV0V+gSNa0ilmQyMtDmbZanp+BuCdtelapKbUr4/A9+tgsYNPAYVhGbfTda24rw2Bca6oN26lG9jPz7/fqSUxjUsNJMCaFangt8RtFuT8j0b+J5M+Tx8owSNa1hoxvcDTag07SnfM/0A5SGS0iSNC81YQePixqCC0NRGTlKaRNAMFJp9aUTQOCnNUBRHNY1TehI06HbPomsbKY2T0ggapDRSGnRjeezhdgnTUzfws55GKAEQJYem11bV2+0ZrFl2I7oeYYkNRYmh6TPf3KC9v89Y0MSGosTQ/GUpaqm1HWEbEgia2FBUoKbZYztMbDaQBE1sKCoATV9OpsdyBQ2CJraiSGnQ9mmCBqUnKQ2qaWKnHaUnQgi1u6egQaMnKQ0acsdOOUpP6NYIQYMm98pkfhOAjFnf9YFfM8JJQde+wtCMzlgOWCtoXPHXPnbwhwnO1cFioV/DuVLQOEEzAHDagHvSW6QpPTkpzRBUqH0QGwBolTtpkvR0AGj622qkI31LhXaNcIIGuCUFxvIAmptTvhUp4HQ/TdKcSjPRtlTzu5CvSO09PM7OIthq9dAUQeOkNMMETkqTNKHSCBpX3PWJHaAympTGCRpB46Q0UhoXV4ljp4IymtKTEzSCxklppDRO6alC6ak156cra+Z3ntDBpwUGdHQd+mjJOCRskT9IHnjUFkNX52B+ofQhO+8hPOdBTyMUEFR/ou30gmwKcGmG0iw1/7ScbaqdAPywoMkflMm2D4z/D30P+Aj4sADbCXyZcfCpfwbp44Ls0/3sqKXnnoaSioDbgNftFLjwuJ1msF3oWe5BQzMD+KQEwRM0SRCXChw9+EEJgidoEkETG4Y+paf6Ko0vVJ8HOguyLcB24PegT18Uv21bpXUW2K+v3faqpskfmmvrkBb9Qe9fB336LdMurEO/Z6T271MhnBM019cheKfZxtK1PnuzzvZuy7/fVjvAVdAM4yJOt/mYEJob6xC8uUBP0KcP5EV16Pfc1G/1h9S3Ft3voL9n7C9wgIvoZ0y32ZR+jz1FeU0d+p0NdFnQ/EHxX2UdCN9WzPJFt6Wl3aay82LHoVLQyFwpr0H0LyBzlbsG/wJe5eWbhdQZ/AAAAABJRU5ErkJggg==";
const iconBalcony = "data:image/png;base64,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";

// ============================================================================
// Types
// ============================================================================

interface Floorplan {
  floorplan_id?: string | null;
  floorplan_name?: string | null;
  floorplan_image_url?: string | null;
  floorplan_beds_min?: string | null;
  floorplan_beds_max?: string | null;
  floorplan_baths_min?: string | null;
  floorplan_sqft_min?: string | null;
  floorplan_sqft_max?: string | null;
  floorplan_price_min?: string | null;
  floorplan_price_max?: string | null;
  floorplan_available_units?: string | null;
}

// ============================================================================
// Photo tiles (literals; extractor cannot follow a factory)
// ============================================================================

const photo1Def: SlotGroupDefinition = {
  label: 'Studio Photo 1',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[0].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[0].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'A photo of a real studio or compact apartment interior at this property — kitchen, living area, or open-concept space. Do not invent a space.',
    context:
      'Photo 1 of 4 in the EXPERIENCE / URBAN STUDIO LIVING gallery on the Urban Studio Life page. Different from photos 2–4.',
  },
};

const photo2Def: SlotGroupDefinition = {
  label: 'Studio Photo 2',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[1].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[1].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 1,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'Another unused studio or compact apartment interior photo from this property — a different room from photo 1.',
    context:
      'Photo 2 of 4 in the EXPERIENCE / URBAN STUDIO LIVING gallery on the Urban Studio Life page. Do not reuse photos 1, 3, or 4.',
  },
};

const photo3Def: SlotGroupDefinition = {
  label: 'Studio Photo 3',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[2].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[2].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 2,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'A third unused studio or compact apartment interior photo from this property — a different room from photos 1 and 2.',
    context:
      'Photo 3 of 4 in the EXPERIENCE / URBAN STUDIO LIVING gallery on the Urban Studio Life page. Do not reuse photos 1, 2, or 4.',
  },
};

const photo4Def: SlotGroupDefinition = {
  label: 'Studio Photo 4',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[3].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[3].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 3,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'A fourth unused studio or compact apartment interior photo from this property — a different room from photos 1–3.',
    context:
      'Photo 4 of 4 in the EXPERIENCE / URBAN STUDIO LIVING gallery on the Urban Studio Life page. Do not reuse photos 1–3.',
  },
};

const waitlistDef: SlotGroupDefinition = {
  label: 'Waitlist Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/floor-plans' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'View Floor Plans' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

const scheduleTourDef: SlotGroupDefinition = {
  label: 'Schedule Tour CTA',
  fields: {
    url: { label: 'Button URL', type: 'url', defaultValue: '/schedule-a-tour' },
    label: { label: 'Button Label', type: 'text', defaultValue: 'Schedule Tour' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
    vibesite_event: {
      label: 'Tracked Event',
      type: 'event',
      defaultValue: 'schedule_tour_clicked',
    },
  },
};

const contactDef: SlotGroupDefinition = {
  label: 'Contact CTA',
  fields: {
    url: { label: 'Button URL', type: 'url', defaultValue: '/contact-us' },
    label: { label: 'Button Label', type: 'text', defaultValue: 'Contact Us' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

const amenitiesCtaDef: SlotGroupDefinition = {
  label: 'Amenities CTA',
  fields: {
    url: { label: 'Button URL', type: 'url', defaultValue: '/amenities' },
    label: { label: 'Button Label', type: 'text', defaultValue: 'Explore Our Amenities' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

// ============================================================================
// Helpers
// ============================================================================

function isUrbanStudioPlan(plan: Floorplan): boolean {
  const name = (plan.floorplan_name || '').toLowerCase();
  const beds = parseFloat(String(plan.floorplan_beds_min ?? ''));
  if (name.includes('urban studio') || name.includes('lagoon') || name.includes('carver')) {
    return true;
  }
  if (name.includes('studio') && !name.includes('penthouse') && !name.includes('townhome')) {
    return true;
  }
  return !Number.isNaN(beds) && beds === 0;
}

function formatPrice(raw?: string | null): string | null {
  if (!raw) return null;
  const n = parseInt(String(raw).replace(/[^0-9]/g, ''), 10);
  if (isNaN(n) || n <= 0) return null;
  return `$${n.toLocaleString()}`;
}

function formatBeds(raw?: string | null): string {
  if (!raw) return '—';
  const n = parseFloat(String(raw));
  if (isNaN(n)) return String(raw);
  if (n === 0) return 'Studio';
  return `${n} bed`;
}

function formatBaths(raw?: string | null): string {
  if (!raw) return '—';
  const n = parseFloat(String(raw));
  if (isNaN(n)) return String(raw);
  return `${n} bath`;
}

function formatSqft(min?: string | null, max?: string | null): string {
  const a = min ? parseInt(String(min), 10) : NaN;
  const b = max ? parseInt(String(max), 10) : NaN;
  if (!isNaN(a) && !isNaN(b) && a !== b) return `${a.toLocaleString()}–${b.toLocaleString()} SQ.FT.`;
  if (!isNaN(a)) return `${a.toLocaleString()} SQ.FT.`;
  if (!isNaN(b)) return `${b.toLocaleString()} SQ.FT.`;
  return '—';
}

// ============================================================================
// Photo tile
// ============================================================================

function PhotoTile({
  path,
  definition,
}: {
  path: string;
  definition: SlotGroupDefinition;
}) {
  const photo = useSlotGroup(path, definition);
  const editMode = useEditMode();
  const isEditing = !!editMode?.isEditMode;
  const src = photo.image || '';

  if (!src && !isEditing) return null;

  return (
    <div className="urbanstudio__photo" data-reveal>
      <EditableSlotGroup groupPath={path} definition={definition}>
        {src ? (
          <VibesitesImage
            url={src}
            alt={photo.alt || ''}
            sizes="(min-width: 992px) 12vw, (min-width: 768px) 23vw, 45vw"
            className="urbanstudio__photo-img"
          />
        ) : (
          <div className="urbanstudio__photo-placeholder" aria-hidden="true" />
        )}
      </EditableSlotGroup>
    </div>
  );
}

// ============================================================================
// Plan card (right column)
// ============================================================================

function PlanCard({ plans }: { plans: Floorplan[] }) {
  const [index, setIndex] = useState(0);
  const editMode = useEditMode();
  const isEditing = !!editMode?.isEditMode;

  const waitlist = useSlotGroup('urbanstudiolife.waitlist', waitlistDef);
  const scheduleTour = useSlotGroup('urbanstudiolife.scheduleTour', scheduleTourDef);
  const contact = useSlotGroup('urbanstudiolife.contact', contactDef);

  const count = plans.length;
  const plan = count > 0 ? plans[Math.min(index, count - 1)] : null;

  if (!plan && !isEditing) return null;

  const price = formatPrice(plan?.floorplan_price_min || plan?.floorplan_price_max);
  const specs = [
    formatBeds(plan?.floorplan_beds_min),
    formatBaths(plan?.floorplan_baths_min),
    formatSqft(plan?.floorplan_sqft_min, plan?.floorplan_sqft_max),
  ].join(' - ');

  const imageSrc = plan?.floorplan_image_url || '';

  return (
    <EditableCollectionBinding
      bindingPath="urbanstudiolife.plans"
      label="Urban Studio Floor Plans"
      className="urbanstudio-card"
    >
      <h3 className="urbanstudio-card__name">
        {plan?.floorplan_name || 'Urban Studio'}
      </h3>

      <EditableSlotGroup
        groupPath="urbanstudiolife.waitlist"
        definition={waitlistDef}
        className="urbanstudio-card__waitlist-wrap"
      >
        <a
          href={waitlist.url || '/floor-plans'}
          target={waitlist._target || '_self'}
          className="urbanstudio-card__waitlist"
        >
          {waitlist.label || 'View Floor Plans'}
        </a>
      </EditableSlotGroup>

      {price ? (
        <p className="urbanstudio-card__price">Starting at {price}</p>
      ) : (
        <p className="urbanstudio-card__price">Call for Pricing</p>
      )}

      <div className="urbanstudio-card__carousel">
        {count > 1 ? (
          <button
            type="button"
            className="urbanstudio-card__arrow urbanstudio-card__arrow--prev"
            aria-label="Previous floor plan"
            onClick={() => setIndex((i) => (i - 1 + count) % count)}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
        ) : null}

        <div className="urbanstudio-card__image-wrap">
          {imageSrc ? (
            <VibesitesImage
              url={imageSrc}
              alt={plan?.floorplan_name || 'Urban studio floor plan'}
              sizes="(min-width: 992px) 28vw, 90vw"
              className="urbanstudio-card__image"
            />
          ) : (
            <div className="urbanstudio-card__image-placeholder" aria-hidden="true" />
          )}
        </div>

        {count > 1 ? (
          <button
            type="button"
            className="urbanstudio-card__arrow urbanstudio-card__arrow--next"
            aria-label="Next floor plan"
            onClick={() => setIndex((i) => (i + 1) % count)}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        ) : null}
      </div>

      <p className="urbanstudio-card__specs">{specs}</p>

      <div className="urbanstudio-card__actions">
        <EditableSlotGroup
          groupPath="urbanstudiolife.scheduleTour"
          definition={scheduleTourDef}
          className="urbanstudio-card__action"
        >
          <a
            href={scheduleTour.url || '/schedule-a-tour'}
            target={scheduleTour._target || '_self'}
            className="urbanstudio-card__btn urbanstudio-card__btn--dark"
            data-vibesite-event={scheduleTour.vibesite_event || undefined}
          >
            {scheduleTour.label || 'Schedule Tour'}
          </a>
        </EditableSlotGroup>

        <EditableSlotGroup
          groupPath="urbanstudiolife.contact"
          definition={contactDef}
          className="urbanstudio-card__action"
        >
          <a
            href={contact.url || '/contact-us'}
            target={contact._target || '_self'}
            className="urbanstudio-card__btn urbanstudio-card__btn--gold"
          >
            {contact.label || 'Contact Us'}
          </a>
        </EditableSlotGroup>
      </div>
    </EditableCollectionBinding>
  );
}

// ============================================================================
// Component
// ============================================================================

export function UrbanStudioLife() {
  const headline = useContentToken('urbanstudiolife.headline');
  const featuresHeading = useContentToken('urbanstudiolife.featuresHeading');
  const highlight1Title = useContentToken('urbanstudiolife.highlight1Title');
  const highlight2Title = useContentToken('urbanstudiolife.highlight2Title');
  const highlight3Title = useContentToken('urbanstudiolife.highlight3Title');
  const highlight4Title = useContentToken('urbanstudiolife.highlight4Title');
  const amenitiesCta = useSlotGroup('urbanstudiolife.amenitiesCta', amenitiesCtaDef);

  const allPlans = useCollectionBinding<Floorplan>('urbanstudiolife.plans', {
    collection: 'floorplans',
  });

  const studioPlans = useMemo(() => {
    const pool = (allPlans ?? []).filter((p) => p?.floorplan_name);
    const tagged = pool.filter(isUrbanStudioPlan);
    return tagged.length > 0 ? tagged : pool.slice(0, 1);
  }, [allPlans]);

  const sectionRef = useScrollReveal<HTMLElement>();
  const galleryRef = useStaggerReveal<HTMLDivElement>({ staggerMs: 70 });
  const highlightsRef = useStaggerReveal<HTMLDivElement>({ staggerMs: 80 });

  return (
    <section ref={sectionRef} className="urbanstudio">
      <div className="urbanstudio__layout">
        {/* ── Left: copy + gallery + features ── */}
        <div className="urbanstudio__main">
          <div className="urbanstudio-intro__heading-block">
            <RichTextContent
              path="urbanstudiolife.eyebrow"
              label="Eyebrow"
              as="p"
              className="urbanstudio-intro__eyebrow"
            />
            <EditableToken path="urbanstudiolife.headline" as="h2" className="urbanstudio-intro__headline">
              {headline || 'URBAN STUDIO LIVING'}
            </EditableToken>
          </div>

          <div ref={galleryRef} className="urbanstudio__gallery">
            <PhotoTile path="urbanstudiolife.photo1" definition={photo1Def} />
            <PhotoTile path="urbanstudiolife.photo2" definition={photo2Def} />
            <PhotoTile path="urbanstudiolife.photo3" definition={photo3Def} />
            <PhotoTile path="urbanstudiolife.photo4" definition={photo4Def} />
          </div>

          <div className="urbanstudio-features__block">
            <EditableToken
              path="urbanstudiolife.featuresHeading"
              as="h3"
              className="urbanstudio-features__heading"
            >
              {featuresHeading || 'DAYMARK UPTOWN URBAN STUDIOS COMING SOON'}
            </EditableToken>

            <div ref={highlightsRef} className="urbanstudio__highlights">
              <div className="urbanstudio__highlight" data-reveal>
                <a
                  href="/amenities"
                  className="urbanstudio__highlight-media"
                  aria-label={highlight1Title || '24 Hour Community Room Access'}
                >
                  <VibesitesImage
                    url={iconKitchen}
                    alt=""
                    width={141}
                    height={96}
                    sizes="140px"
                    className="urbanstudio__highlight-icon"
                  />
                </a>
                <EditableToken
                  path="urbanstudiolife.highlight1Title"
                  as="span"
                  className="urbanstudio__highlight-title"
                >
                  {highlight1Title || '24 Hour Community Room Access'}
                </EditableToken>
              </div>

              <div className="urbanstudio__highlight" data-reveal>
                <a
                  href="/amenities"
                  className="urbanstudio__highlight-media"
                  aria-label={highlight2Title || 'Stainless-Steel Appliances'}
                >
                  <VibesitesImage
                    url={iconSteel}
                    alt=""
                    width={141}
                    height={102}
                    sizes="140px"
                    className="urbanstudio__highlight-icon"
                  />
                </a>
                <EditableToken
                  path="urbanstudiolife.highlight2Title"
                  as="span"
                  className="urbanstudio__highlight-title"
                >
                  {highlight2Title || 'Stainless-Steel Appliances'}
                </EditableToken>
              </div>

              <div className="urbanstudio__highlight" data-reveal>
                <a
                  href="/amenities"
                  className="urbanstudio__highlight-media"
                  aria-label={highlight3Title || 'Modern Closet Space'}
                >
                  <VibesitesImage
                    url={iconClosets}
                    alt=""
                    width={141}
                    height={81}
                    sizes="140px"
                    className="urbanstudio__highlight-icon"
                  />
                </a>
                <EditableToken
                  path="urbanstudiolife.highlight3Title"
                  as="span"
                  className="urbanstudio__highlight-title"
                >
                  {highlight3Title || 'Modern Closet Space'}
                </EditableToken>
              </div>

              <div className="urbanstudio__highlight" data-reveal>
                <a
                  href="/amenities"
                  className="urbanstudio__highlight-media"
                  aria-label={highlight4Title || 'Community Outdoor Balcony'}
                >
                  <VibesitesImage
                    url={iconBalcony}
                    alt=""
                    width={141}
                    height={92}
                    sizes="140px"
                    className="urbanstudio__highlight-icon"
                  />
                </a>
                <EditableToken
                  path="urbanstudiolife.highlight4Title"
                  as="span"
                  className="urbanstudio__highlight-title"
                >
                  {highlight4Title || 'Community Outdoor Balcony'}
                </EditableToken>
              </div>
            </div>

            <RichTextContent
              path="urbanstudiolife.featureList"
              label="Feature List"
              as="div"
              className="urbanstudio__feature-list"
            />

            <EditableSlotGroup
              groupPath="urbanstudiolife.amenitiesCta"
              definition={amenitiesCtaDef}
              className="urbanstudio__amenities-cta-wrap"
            >
              <a
                href={amenitiesCta.url || '/amenities'}
                target={amenitiesCta._target || '_self'}
                className="urbanstudio__amenities-cta"
              >
                <span>{amenitiesCta.label || 'Explore Our Amenities'}</span>
                <svg
                  className="urbanstudio__amenities-cta-icon"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M5 12h14M13 6l6 6-6 6"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </a>
            </EditableSlotGroup>
          </div>
        </div>

        {/* ── Right: plan card ── */}
        <aside className="urbanstudio__aside">
          <PlanCard plans={studioPlans} />
        </aside>
      </div>

      {/* Schema-lint anchors */}
      <EditableCollectionBinding
        bindingPath="urbanstudiolife.plans"
        label="Urban Studio Floor Plans"
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableCollectionBinding>
      <EditableSlotGroup
        groupPath="urbanstudiolife.waitlist"
        definition={waitlistDef}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup
        groupPath="urbanstudiolife.scheduleTour"
        definition={scheduleTourDef}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup
        groupPath="urbanstudiolife.contact"
        definition={contactDef}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup
        groupPath="urbanstudiolife.photo1"
        definition={photo1Def}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup
        groupPath="urbanstudiolife.photo2"
        definition={photo2Def}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup
        groupPath="urbanstudiolife.photo3"
        definition={photo3Def}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup
        groupPath="urbanstudiolife.photo4"
        definition={photo4Def}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>

      <style>{`
        /* Spacing mirrors Tower 12:
           section 100px/40px; gallery mt 25px / mb 10px;
           features heading mt 20px; icons + list tucked tight. */
        .urbanstudio {
          background: #fff;
          padding: 3.25rem 1.25rem 3.5rem;
        }
        @media (min-width: 768px) {
          .urbanstudio { padding: 4.5rem 2rem 5rem; }
        }
        @media (min-width: 1200px) {
          .urbanstudio { padding: 100px 40px; }
        }

        .urbanstudio__layout {
          max-width: 1400px;
          margin: 0 auto;
          display: grid;
          gap: 2rem;
          align-items: start;
        }
        @media (min-width: 992px) {
          .urbanstudio__layout {
            grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
            gap: 1.25rem 1.5rem;
          }
        }

        .urbanstudio__main {
          min-width: 0;
        }

        .urbanstudio-intro__heading-block {
          border-left: 10px solid #030303;
          padding: 0 0 0 10px;
          text-align: left;
          max-width: 36rem;
          margin: 0;
        }

        .urbanstudio-intro__eyebrow {
          margin: 0;
          font-family: var(--vibe-font-heading);
          font-size: 1.15rem;
          font-weight: 400;
          letter-spacing: 0.02em;
          line-height: 1.3;
          text-transform: uppercase;
          color: var(--vibe-color-primary);
        }
        .urbanstudio-intro__eyebrow p { margin: 0; }
        @media (min-width: 768px) {
          .urbanstudio-intro__eyebrow { font-size: 1.45rem; }
        }

        .urbanstudio-intro__headline {
          margin: 0.125rem 0 0;
          font-family: var(--vibe-font-heading);
          font-size: 2.5rem;
          font-weight: 700;
          letter-spacing: 0;
          line-height: 1;
          text-transform: uppercase;
          color: #b6b6b6;
        }
        @media (min-width: 768px) {
          .urbanstudio-intro__headline { font-size: 3.75rem; }
        }
        @media (min-width: 1200px) {
          .urbanstudio-intro__headline { font-size: 5rem; }
        }

        .urbanstudio__gallery {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 5px;
          margin: 25px 0 10px;
        }
        @media (min-width: 768px) {
          .urbanstudio__gallery {
            grid-template-columns: repeat(4, minmax(0, 1fr));
          }
        }

        .urbanstudio__photo {
          display: block;
          position: relative;
          aspect-ratio: 1 / 1;
          overflow: hidden;
          background: var(--vibe-color-surface);
        }
        .urbanstudio__photo > * {
          display: block;
          width: 100%;
          height: 100%;
        }
        .urbanstudio__photo-img {
          display: block;
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
        }
        .urbanstudio__photo-placeholder {
          width: 100%;
          height: 100%;
          background: var(--vibe-color-surface);
        }

        .urbanstudio-features__block {
          margin-top: 20px;
        }

        .urbanstudio-features__heading {
          margin: 0 0 1rem;
          font-family: var(--vibe-font-body);
          font-size: 0.875rem;
          font-weight: 500;
          letter-spacing: 0.12em;
          line-height: 1.4;
          text-align: center;
          text-transform: uppercase;
          color: var(--vibe-color-primary);
        }
        @media (min-width: 768px) {
          .urbanstudio-features__heading {
            margin-bottom: 1.15rem;
            font-size: 0.875rem;
            letter-spacing: 0.14em;
          }
        }

        .urbanstudio__highlights {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1.25rem 0.75rem;
          margin: 0 0 1.15rem;
        }
        @media (min-width: 768px) {
          .urbanstudio__highlights {
            grid-template-columns: repeat(4, minmax(0, 1fr));
            gap: 0.75rem 0.5rem;
            margin-bottom: 1rem;
          }
        }

        .urbanstudio__highlight {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.55rem;
          color: #000;
          text-align: center;
        }
        .urbanstudio__highlight-media {
          display: flex;
          align-items: center;
          justify-content: center;
          text-decoration: none;
        }
        .urbanstudio__highlight:hover .urbanstudio__highlight-title {
          color: var(--vibe-color-secondary);
        }

        .urbanstudio__highlight-icon {
          display: block;
          width: auto;
          max-width: 5.5rem;
          height: 3.25rem;
          object-fit: contain;
          object-position: center;
        }
        @media (min-width: 768px) {
          .urbanstudio__highlight-icon {
            max-width: 6.25rem;
            height: 3.5rem;
          }
        }

        .urbanstudio__highlight-title {
          display: block;
          font-family: var(--vibe-font-heading);
          font-size: 0.7rem;
          font-weight: 500;
          letter-spacing: 0.04em;
          line-height: 1.3;
          text-transform: uppercase;
          color: #000;
          transition: color 0.2s ease;
        }
        @media (min-width: 768px) {
          .urbanstudio__highlight-title { font-size: 0.75rem; }
        }

        .urbanstudio__feature-list {
          font-family: var(--vibe-font-body);
          font-size: 0.9375rem;
          font-weight: 400;
          line-height: 1.65;
          color: #4b4d51;
        }
        @media (min-width: 768px) {
          .urbanstudio__feature-list {
            column-count: 2;
            column-gap: 2rem;
          }
        }
        .urbanstudio__feature-list p { margin: 0 0 0.5rem; }
        .urbanstudio__feature-list ul {
          list-style: disc;
          list-style-position: outside;
          margin: 0;
          padding: 0 0 0 1.15rem;
        }
        .urbanstudio__feature-list li {
          margin: 0 0 0.35rem;
          break-inside: avoid;
          color: #4b4d51;
        }
        .urbanstudio__feature-list li::marker { color: #4b4d51; }

        .urbanstudio__amenities-cta-wrap {
          display: flex;
          justify-content: center;
          margin: 1.75rem auto 0;
        }
        @media (min-width: 768px) {
          .urbanstudio__amenities-cta-wrap { margin-top: 2rem; }
        }

        .urbanstudio__amenities-cta {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.65rem;
          min-width: 16.5rem;
          height: 60px;
          padding: 0 1.75rem;
          background: var(--vibe-color-secondary);
          color: var(--vibe-color-text-on-secondary);
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          font-weight: 700;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          text-decoration: none;
          border: none;
          border-radius: 0;
          transition: background 0.25s ease, color 0.25s ease;
        }
        .urbanstudio__amenities-cta:hover {
          background: #b6b6b6;
          color: #000;
        }
        .urbanstudio__amenities-cta-icon { flex-shrink: 0; }

        /* ── Right plan card ── */
        .urbanstudio__aside {
          min-width: 0;
        }

        .urbanstudio-card {
          display: flex;
          flex-direction: column;
          align-items: center;
          box-sizing: border-box;
          width: 100%;
          padding: 1.5rem 1.25rem 1.35rem;
          border: 1px solid #d9d9d9;
          background: #fff;
          text-align: center;
        }

        .urbanstudio-card__name {
          margin: 0 0 0.2rem;
          font-family: var(--vibe-font-heading);
          font-size: 1.1rem;
          font-weight: 700;
          letter-spacing: 0.04em;
          line-height: 1.25;
          text-transform: uppercase;
          color: var(--vibe-color-primary);
        }

        .urbanstudio-card__waitlist-wrap {
          display: block;
          margin: 0.1rem 0 0.65rem;
        }
        .urbanstudio-card__waitlist {
          font-family: var(--vibe-font-body);
          font-size: 0.875rem;
          font-weight: 400;
          color: #4b4d51;
          text-decoration: underline;
          text-underline-offset: 3px;
        }
        .urbanstudio-card__waitlist:hover { color: var(--vibe-color-secondary); }

        .urbanstudio-card__price {
          margin: 0 0 0.85rem;
          font-family: var(--vibe-font-heading);
          font-size: 1.05rem;
          font-weight: 700;
          letter-spacing: 0.01em;
          color: #000;
        }

        .urbanstudio-card__carousel {
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.25rem;
          width: 100%;
          margin-bottom: 0.65rem;
        }

        .urbanstudio-card__image-wrap {
          flex: 1;
          min-width: 0;
          max-width: 240px;
          aspect-ratio: 1 / 1;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .urbanstudio-card__image {
          display: block;
          width: 100%;
          height: 100%;
          object-fit: contain;
          object-position: center;
        }
        .urbanstudio-card__image-placeholder {
          width: 100%;
          height: 100%;
          background: #f3f3f3;
        }

        .urbanstudio-card__arrow {
          flex-shrink: 0;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 2rem;
          height: 2rem;
          padding: 0;
          border: 1.5px solid #000;
          border-radius: 999px;
          background: #fff;
          color: #000;
          cursor: pointer;
        }
        .urbanstudio-card__arrow:hover {
          background: #000;
          color: #fff;
        }

        .urbanstudio-card__specs {
          margin: 0 0 1rem;
          font-family: var(--vibe-font-body);
          font-size: 0.875rem;
          font-weight: 400;
          letter-spacing: 0.02em;
          color: #4b4d51;
        }

        .urbanstudio-card__actions {
          display: flex;
          flex-direction: column;
          gap: 0.55rem;
          width: 100%;
        }
        .urbanstudio-card__action { display: block; width: 100%; }

        .urbanstudio-card__btn {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          height: 46px;
          padding: 0 1rem;
          font-family: var(--vibe-font-body);
          font-size: 0.8rem;
          font-weight: 700;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          text-decoration: none;
          border: none;
          border-radius: 0;
          transition: background 0.25s ease, color 0.25s ease;
        }
        .urbanstudio-card__btn--dark {
          background: #3a3a3a;
          color: #fff;
        }
        .urbanstudio-card__btn--dark:hover {
          background: #000;
          color: #fff;
        }
        .urbanstudio-card__btn--gold {
          background: var(--vibe-color-secondary);
          color: var(--vibe-color-text-on-secondary);
        }
        .urbanstudio-card__btn--gold:hover {
          background: #b6b6b6;
          color: #000;
        }

        /* Scroll reveals */
        .urbanstudio-intro__heading-block,
        .urbanstudio-features__heading,
        .urbanstudio__feature-list,
        .urbanstudio__amenities-cta-wrap,
        .urbanstudio-card {
          transition: opacity 1.1s cubic-bezier(0.22, 1, 0.36, 1),
                      transform 1.1s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .urbanstudio.reveal--hidden .urbanstudio-intro__heading-block {
          opacity: 0;
          transform: translateX(-28px);
        }
        .urbanstudio.reveal--hidden .urbanstudio-features__heading,
        .urbanstudio.reveal--hidden .urbanstudio__feature-list {
          opacity: 0;
          transform: translateY(24px);
        }
        .urbanstudio.reveal--hidden .urbanstudio__amenities-cta-wrap {
          opacity: 0;
          transform: scale(0.65);
        }
        .urbanstudio.reveal--hidden .urbanstudio-card {
          opacity: 0;
          transform: translateX(28px);
        }
        .urbanstudio.reveal--visible .urbanstudio-intro__heading-block,
        .urbanstudio.reveal--visible .urbanstudio-features__heading,
        .urbanstudio.reveal--visible .urbanstudio__feature-list,
        .urbanstudio.reveal--visible .urbanstudio__amenities-cta-wrap,
        .urbanstudio.reveal--visible .urbanstudio-card {
          opacity: 1;
          transform: none;
        }
        .urbanstudio.reveal--visible .urbanstudio-features__heading { transition-delay: 0.1s; }
        .urbanstudio.reveal--visible .urbanstudio__feature-list { transition-delay: 0.2s; }
        .urbanstudio.reveal--visible .urbanstudio__amenities-cta-wrap { transition-delay: 0.3s; }
        .urbanstudio.reveal--visible .urbanstudio-card { transition-delay: 0.15s; }

        .urbanstudio__gallery [data-reveal],
        .urbanstudio__highlights [data-reveal] {
          transition: opacity 0.9s cubic-bezier(0.22, 1, 0.36, 1),
                      transform 0.9s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .urbanstudio__gallery [data-reveal].reveal--hidden,
        .urbanstudio__highlights [data-reveal].reveal--hidden {
          opacity: 0;
          transform: translateY(24px);
        }
        .urbanstudio__gallery [data-reveal].reveal--visible,
        .urbanstudio__highlights [data-reveal].reveal--visible {
          opacity: 1;
          transform: none;
        }

        @media (prefers-reduced-motion: reduce) {
          .urbanstudio-intro__heading-block,
          .urbanstudio-features__heading,
          .urbanstudio__feature-list,
          .urbanstudio__amenities-cta-wrap,
          .urbanstudio-card,
          .urbanstudio__gallery [data-reveal],
          .urbanstudio__highlights [data-reveal],
          .urbanstudio.reveal--hidden .urbanstudio-intro__heading-block,
          .urbanstudio.reveal--hidden .urbanstudio-features__heading,
          .urbanstudio.reveal--hidden .urbanstudio__feature-list,
          .urbanstudio.reveal--hidden .urbanstudio__amenities-cta-wrap,
          .urbanstudio.reveal--hidden .urbanstudio-card {
            opacity: 1;
            transform: none;
            transition: none;
          }
          .urbanstudio-card__btn,
          .urbanstudio__amenities-cta { transition: none; }
        }
      `}</style>
    </section>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'UrbanStudioLife',
  displayName: 'Urban Studio Life',
  tokens: {
    'urbanstudiolife.eyebrow': {
      label: 'Eyebrow',
      type: 'richtext',
      defaultValue:
        '<p>EXPERIENCE <span data-type="field-token" data-field="property_name"></span></p>',
    },
    'urbanstudiolife.headline': {
      label: 'Headline',
      type: 'text',
      defaultValue: 'URBAN STUDIO LIVING',
    },
    'urbanstudiolife.featuresHeading': {
      label: 'Features Heading',
      type: 'text',
      defaultValue: 'DAYMARK UPTOWN URBAN STUDIOS COMING SOON',
    },
    'urbanstudiolife.highlight1Title': {
      label: 'Highlight 1 Title',
      type: 'text',
      defaultValue: '24 Hour Community Room Access',
    },
    'urbanstudiolife.highlight2Title': {
      label: 'Highlight 2 Title',
      type: 'text',
      defaultValue: 'Stainless-Steel Appliances',
    },
    'urbanstudiolife.highlight3Title': {
      label: 'Highlight 3 Title',
      type: 'text',
      defaultValue: 'Modern Closet Space',
    },
    'urbanstudiolife.highlight4Title': {
      label: 'Highlight 4 Title',
      type: 'text',
      defaultValue: 'Community Outdoor Balcony',
    },
    'urbanstudiolife.featureList': {
      label: 'Feature List',
      type: 'richtext',
      defaultValue:
        '<ul><li>Kitchen includes Stainless Steel microwave, compact refrigerator and freezer, with elegant Quartz countertops and under mount sink</li><li>Bathrooms are full sized with Garden Soaking tub/shower; equipped with floor to ceiling arctic white tile surrounding and a 6″ Rain Flurry shower head</li><li>Urban Studio Community Room Access:</li><li>You will have 24/7 access to the Community Room on your floor level including a Kitchen, Laundry Room, Dining Table, and outdoor Balcony</li><li>Kitchen includes Stainless Steel Whirlpool Stove/Oven, Refrigerator, Microwave and Dishwasher</li><li>Laundry room includes free laundry with two sets of full-size washer/dryers</li></ul>',
    },
  },
  slotGroups: {
    'urbanstudiolife.photo1': photo1Def,
    'urbanstudiolife.photo2': photo2Def,
    'urbanstudiolife.photo3': photo3Def,
    'urbanstudiolife.photo4': photo4Def,
    'urbanstudiolife.waitlist': waitlistDef,
    'urbanstudiolife.scheduleTour': scheduleTourDef,
    'urbanstudiolife.contact': contactDef,
    'urbanstudiolife.amenitiesCta': amenitiesCtaDef,
  },
  collectionBindings: {
    'urbanstudiolife.plans': {
      label: 'Urban Studio Floor Plans',
      collection: 'floorplans',
      description:
        'Studio plans shown in the right-hand card. Urban studio, Lagoon, Carver, and zero-bedroom plans are preferred; falls back to the first plan when none match.',
    },
  },
};

(UrbanStudioLife as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default UrbanStudioLife;
